import { useCallback, useEffect, useRef, useState } from "react";
import type { CachedAttendee } from "~/lib/offline/db";
import { enqueueCheckIn, getAttendeeByCode, getMeta, setMeta } from "~/lib/offline/store";
import { useSync } from "~/providers/sync-provider";
import { useTRPC } from "~utils/trpc/query-client";
import { SyncIndicator } from "../components/sync-indicator";
import { useQrScanner } from "../components/use-qr-scanner";

type KioskSettings = {
	kioskSearchEnabled: boolean;
};

const SETTINGS_META_KEY = "settings";
const SUCCESS_RESET_MS = 3000;

type Stage =
	| { kind: "prompt" }
	| { kind: "confirm"; attendee: CachedAttendee }
	| { kind: "success"; name: string }
	| { kind: "notFound" };

export default function KioskScreen({ eventId }: { eventId: string }) {
	const trpc = useTRPC();
	const { syncNow } = useSync();

	const { data: serverSettings } = trpc.checkIn.getSettings.useQuery(
		{ eventId },
		{ retry: false },
	);
	const [kioskSearchEnabled, setKioskSearchEnabled] = useState(false);

	useEffect(() => {
		if (serverSettings) {
			setKioskSearchEnabled(serverSettings.kioskSearchEnabled);
			void setMeta(eventId, SETTINGS_META_KEY, JSON.stringify(serverSettings));
			return;
		}
		void (async () => {
			const cached = await getMeta(eventId, SETTINGS_META_KEY);
			if (cached) {
				setKioskSearchEnabled((JSON.parse(cached) as KioskSettings).kioskSearchEnabled);
			}
		})();
	}, [serverSettings, eventId]);

	const [stage, setStage] = useState<Stage>({ kind: "prompt" });
	const [entry, setEntry] = useState("");
	const resetTimer = useRef<number | null>(null);

	const reset = useCallback(() => {
		setEntry("");
		setStage({ kind: "prompt" });
	}, []);

	const resolve = useCallback(
		async (code: string) => {
			const attendee = await getAttendeeByCode(eventId, code);
			if (!attendee) {
				setStage({ kind: "notFound" });
				return;
			}
			setStage({ kind: "confirm", attendee });
		},
		[eventId],
	);

	const videoRef = useQrScanner(resolve, stage.kind === "prompt");

	const confirm = useCallback(
		async (attendee: CachedAttendee) => {
			const now = new Date().toISOString();
			await enqueueCheckIn(eventId, {
				clientId: crypto.randomUUID(),
				attendeeId: attendee.attendeeId,
				direction: "in",
				voidsClientId: null,
				occurredAt: now,
				createdAt: now,
				source: "kiosk",
				synced: 0,
			});
			void syncNow();
			const name =
				`${attendee.firstName ?? ""} ${attendee.lastName ?? ""}`.trim() || "you";
			setStage({ kind: "success", name });
		},
		[eventId, syncNow],
	);

	useEffect(() => {
		if (stage.kind !== "success" && stage.kind !== "notFound") return;
		resetTimer.current = window.setTimeout(reset, SUCCESS_RESET_MS);
		return () => {
			if (resetTimer.current) window.clearTimeout(resetTimer.current);
		};
	}, [stage.kind, reset]);

	const submitEntry = async () => {
		const code = entry.trim();
		if (!code) return;
		await resolve(code);
	};

	return (
		<div className="min-h-dvh bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center">
			<div className="absolute top-4 right-4 z-10">
				<SyncIndicator />
			</div>

			{stage.kind === "prompt" && (
				<div className="w-full max-w-md space-y-6">
					<h1 className="text-3xl font-semibold">Scan your QR code</h1>
					<video
						ref={videoRef}
						className="w-full rounded-2xl bg-black aspect-square object-cover"
					/>
					{kioskSearchEnabled && (
						<div className="space-y-3">
							<input
								className="w-full rounded-lg bg-slate-800 px-4 py-3 text-center text-lg"
								placeholder="Enter your code"
								value={entry}
								onChange={(e) => setEntry(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") void submitEntry();
								}}
								autoComplete="off"
							/>
							<button
								type="button"
								className="w-full rounded-lg bg-slate-700 px-4 py-3 text-lg font-medium hover:bg-slate-600 disabled:opacity-50"
								onClick={() => void submitEntry()}
								disabled={!entry.trim()}
							>
								Find me
							</button>
						</div>
					)}
				</div>
			)}

			{stage.kind === "confirm" && (
				<div className="w-full max-w-md space-y-8">
					<div className="space-y-2">
						<p className="text-xl text-slate-400">Are you</p>
						<p className="text-4xl font-bold">
							{`${stage.attendee.firstName ?? ""} ${stage.attendee.lastName ?? ""}`.trim() ||
								"this attendee"}
							?
						</p>
					</div>
					<button
						type="button"
						className="w-full rounded-2xl bg-green-600 px-6 py-6 text-2xl font-semibold hover:bg-green-700"
						onClick={() => void confirm(stage.attendee)}
					>
						Yes, check me in
					</button>
					<button
						type="button"
						className="w-full text-lg text-slate-400 hover:underline"
						onClick={reset}
					>
						That's not me
					</button>
				</div>
			)}

			{stage.kind === "success" && (
				<div className="space-y-4">
					<div className="text-6xl">✓</div>
					<p className="text-3xl font-semibold">Checked in</p>
					<p className="text-xl text-slate-400">Welcome, {stage.name}!</p>
				</div>
			)}

			{stage.kind === "notFound" && (
				<div className="space-y-4">
					<p className="text-3xl font-semibold">We couldn't find that code</p>
					<p className="text-xl text-slate-400">Please try again or ask a staff member.</p>
				</div>
			)}
		</div>
	);
}
